import React, {
    Dispatch,
    KeyboardEvent,
    memo,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import styles from "./VideoCommentPanel.module.css";
import { useFormTextInput } from "../../../../common/useFormTextInput";
import Button from "../../../../common/components/button/Button";
import { useDispatch, useSelector } from "react-redux";
import { AddVideoComment } from "../../../../common/video/VideoActions";
import CommentCard from "./comment/CommentCard";
import { VideoComment } from "../../../../common/video/models/VideoComment";
import { AppState } from "../../../../root/RootReducer";
import PuiTypography from "../../../../components/elements/PuiTypography";
import PuiButton from "../../../../components/elements/PuiButton";
import { FilledInput, FormControl, makeStyles, createStyles } from "@material-ui/core";

interface VideoCommentPanelProps {
    videoId: number;
    videoRef: RefObject<HTMLVideoElement>;
    setIsUnderlKeyBorardControl: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles(() =>
    createStyles({
        searchTitle: {
            color: "var(--primary-font)!important",
            transform: "none!important",
            height: "10px",
        },
        searchInput: {
            color: "var(--primary-font)",
            width: "100%",
            paddingBottom: "5px",
            background: "none!important",
            "&:before": {
                borderBottom: "0.5px solid var(--gray-42)!important",
            },
            "&:after": {
                borderBottom: "1px solid var(--studio-btn-bg)!important",
            },
            "& input": {
                padding: "0!important",
            },
        },
    })
);

function VideoCommentPanel({
    videoId,
    videoRef,
    setIsUnderlKeyBorardControl,
}: VideoCommentPanelProps) {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [input, updateInput, resetInput] = useFormTextInput();

    const { userInfo, comments } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
        comments: state.video.video?.comments,
    }));

    const [currentComments, setCurrentComments] = useState<VideoComment[]>(comments || []);
    const [isComment, setIsComment] = useState<boolean>(false);

    const cancelComment = () => {
        setIsComment(false);
        resetInput();
        setIsUnderlKeyBorardControl(true);
    };

    const submitComment = useCallback(() => {
        if (videoRef.current) {
            const commentTimeMillis = Math.floor(videoRef.current.currentTime * 1000);
            dispatch(
                AddVideoComment({
                    videoId: videoId,
                    userId: userInfo ? parseInt(userInfo.id) : -1,
                    text: input.trim(),
                    commentTimeMillis: commentTimeMillis,
                    isPrivate: false,
                })
            );
            resetInput();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId, userInfo, input, resetInput, videoRef]);

    const isValidInput = useMemo(() => Boolean(input.trim()), [input]);

    const handleEnterKey = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter" && isValidInput) {
                submitComment();
            }
        },
        [submitComment, isValidInput]
    );

    const SetIsComment = () => {
        setIsComment(true);
        setIsUnderlKeyBorardControl(false);
    };

    const onFocusFunc = () => {
        setIsUnderlKeyBorardControl(false);
    };

    const onBlurFunc = () => {
        setIsUnderlKeyBorardControl(true);
    };

    useEffect(() => {
        if (!comments) return;
        const sortComment = comments.sort(function (a, b) {
            if (a.id < b.id) {
                return 1;
            } else {
                return -1;
            }
        });
        setCurrentComments(sortComment);
    }, [comments]);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div style={{ marginRight: "5px" }}>
                    <PuiTypography size={20}>{String(currentComments.length)}</PuiTypography>
                </div>
                <PuiTypography size={20} bold={true}>
                    件のコメント
                </PuiTypography>
            </div>

            <div className={styles.input}>
                <div className={styles.icon}>
                    <img
                        src={userInfo?.imageUrl}
                        onError={(e) =>
                            (e.currentTarget.src = require("../../../../assets/image/noPresenterImage.svg"))
                        }
                        alt={userInfo?.fullName}
                    />
                </div>

                <div className={styles.inputArea}>
                    <FormControl fullWidth variant="filled" color="secondary">
                        <FilledInput
                            id="searchInput"
                            onChange={updateInput}
                            onKeyPress={handleEnterKey}
                            onClick={SetIsComment}
                            onFocus={onFocusFunc}
                            onBlur={onBlurFunc}
                            placeholder="コメントを追加..."
                            autoComplete={"off"}
                            value={input}
                            className={classes.searchInput}
                        />
                    </FormControl>

                    {isComment && (
                        <div className={styles.inputRight}>
                            <Button
                                onClick={cancelComment}
                                color="white"
                                style={{
                                    border: "none",
                                    bottom: "16px",
                                    marginRight: "15px",
                                    color: "var(--primary-font)",
                                }}
                            >
                                キャンセル
                            </Button>
                            <div className={styles.submitComment}>
                                <PuiButton
                                    isActive={isValidInput}
                                    variant="contained"
                                    onClick={submitComment}
                                >
                                    コメント
                                </PuiButton>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.comments}>
                {currentComments &&
                    currentComments.map((comment) => (
                        <CommentCard
                            key={comment.id}
                            comment={comment}
                            videoRef={videoRef}
                            setIsUnderlKeyBorardControl={setIsUnderlKeyBorardControl}
                        />
                    ))}
            </div>
        </div>
    );
}

export default memo(VideoCommentPanel);
